/**
 * This helper method takes in an array of objects and reduces it to a flat object. Mutiple values are joined into a comma separated string
 * eg: [{id: 'foo', values: [{value: 'bar'}, {value: 'baz'}]}, {id: 'alpha', values: [{value: 'beta'}]}] => {foo: 'bar, baz', alpha: 'beta'}
 * @param {object} appliedFilters: an object that represents all applied filters
 * @returns {object} a flattened version of appliedFilters
 */
export const getQueryFacets = (appliedFilters = []) =>
	/* eslint-disable no-param-reassign */
	appliedFilters.reduce((queryFacetsObj, { id, values }) => {
		queryFacetsObj[id] = values
			.reduce((queries, { value }) => {
				return queries.concat(value);
			}, [])
			.join(', ');
		return queryFacetsObj;
	}, {});

/**
 * Method that fires tracking events to GTMDataLayer
 * @param {object} appliedFilters an object that represents all applied filters
 * @param {object} inventory an object that represents the inventory on the page
 * @param {string} sortedBy a string that represents the applied sort by filters on the page
 */
export const setDataLayerTracking = (
	appliedFilters = [],
	sortBy = [],
	inventory = {},
	eventType,
	eventData,
	publishFilterSearchEvent
) => {
	const {
		pageInfo: { totalCount = 0, pageSize, pageStart },
		trackingData
	} = inventory;
	const isTypedSearch = appliedFilters.some(({ id }) => id === 'search');
	const queryFacets = getQueryFacets(appliedFilters);

	if (
		window &&
		window.DDC &&
		typeof window.DDC.pushToGtmDataLayer === 'function'
	) {
		if (publishFilterSearchEvent && eventType === 'facet') {
			if (isTypedSearch) {
				// this event only fires when text search results in a search param
				window.DDC.pushToGtmDataLayer({
					event: 'ddc.inventory.typedSearch',
					eventData: {
						resultCount: totalCount,
						queryFacets
					}
				});
			} else {
				// this event fires on any other facet interaction
				window.DDC.pushToGtmDataLayer({
					event: 'ddc.inventory.filterSearch.click',
					eventData: {
						facets: queryFacets,
						...eventData
					}
				});
			}

			// this is a search results event and is needed for some legacy tracking frameworks
			window.DDC.pushToGtmDataLayer({
				event: 'ddc.inventory.filterSearch',
				eventData: {
					resultCount: totalCount,
					queryFacets
				}
			});
		} else {
			// TODO: Delete this section once feature flag can be removed
			// eslint-disable-next-line
			if (eventType === 'facet' && !isTypedSearch) {
				window.DDC.pushToGtmDataLayer({
					event: 'ddc.inventory.filterSearch.click',
					eventData: {
						facets: queryFacets,
						...eventData
					}
				});
			} else if (eventType !== 'sort') {
				window.DDC.pushToGtmDataLayer({
					event: 'ddc.inventory.filterSearch',
					eventData: {
						resultCount: totalCount,
						queryFacets,
						sortBy
					}
				});

				if (isTypedSearch) {
					window.DDC.pushToGtmDataLayer({
						event: 'ddc.inventory.typedSearch',
						eventData: {
							resultCount: totalCount,
							queryFacets
						}
					});
				}
			}
		}
	}

	if (window.DDC.dataLayer) {
		window.DDC.dataLayer.vehicles = trackingData;
		window.DDC.dataLayer.page.attributes.vehicleListingPageSort =
			sortBy[0] || null;
		window.DDC.dataLayer.page.attributes.vehicleResultCount = totalCount;
		window.DDC.dataLayer.page.attributes.vehicleCountPerPage = pageSize;
		window.DDC.dataLayer.page.attributes.vehicleCurrentPage =
			pageStart / pageSize + 1;
		window.DDC.dataLayer.page.queryFacets = queryFacets || {};
	}
};
